import { DeclarationDocumentViewer } from "@/components/common/PdfViewer/DeclarationDocumentViewer"
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Download, FileText, Loader2 } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { fetchConformityPdfUrl } from "../../../../api/conformityApi"
import type { FilterToken } from "../../../declarations/review/studio/TableSearch"
import {
  PDF_PANE_ZOOM_DEFAULT,
  PdfPanePageIndicator,
  PdfPaneZoomControl,
  type PdfPaneZoom,
} from "./PdfPaneControls"
// The viewer's `.doc-viewer`/`.doc-scroll` sizing and the find-bar's
// `.doc-bar-*` dock chrome live only here — see ConformityEditorModuleV2's
// FrozenPdfPane, which imports the same file for the same reason.
import "../../../declarations/review/studioScreenA.css"

/** Full-screen overlay for the rendered declaration PDF (frozen bytes for an
 *  issued row, a live render for a draft). Fetches through the authenticated
 *  client and owns the object URL for as long as it is open. */
export function PdfOverlay({
  declarationId,
  lang,
  title,
  onClose,
  onDownload,
  downloadPending = false,
}: {
  declarationId: string
  lang?: string
  title?: string
  onClose: () => void
  onDownload?: () => void
  downloadPending?: boolean
}) {
  const { translate } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)
  const heading = title ?? translate("MC_CONF_V2_EDIT_DOCUMENT")
  const [barOpen, setBarOpen] = useState(false)
  const [tokens, setTokens] = useState<FilterToken[]>([])
  const [search, setSearch] = useState("")
  const [zoom, setZoom] = useState<PdfPaneZoom>(PDF_PANE_ZOOM_DEFAULT)
  const [page, setPage] = useState<{
    current: number | null
    total: number | null
  }>({ current: null, total: null })
  // Full screen shows the WHOLE page: the viewer fits pages to the width of
  // its box, so the box is narrowed to the width whose A4 page height fits the
  // overlay, and centred. Zoom then scales from that fit-page base: the box
  // grows with the zoomed page up to the overlay width, and only the part that
  // still does not fit is left to the viewer's `scale` (and pans).
  const bodyRef = useRef<HTMLDivElement>(null)
  const [body, setBody] = useState<{ width: number; fit: number } | null>(null)
  useEffect(() => {
    const el = bodyRef.current
    if (!el || typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setBody({
        width: Math.floor(width),
        fit: Math.floor(Math.min(width, fitPageWidth(height))),
      })
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [url])
  const { boxWidth, viewerScale } = zoomedBox(body, zoom)

  useEffect(() => {
    let cancelled = false
    let objectUrl: string | null = null
    setUrl(null)
    setFailed(false)
    fetchConformityPdfUrl(declarationId, lang)
      .then((u) => {
        if (cancelled) {
          URL.revokeObjectURL(u)
          return
        }
        objectUrl = u
        setUrl(u)
      })
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [declarationId, lang])

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        className="flex h-[94vh] w-[94vw] max-w-none flex-col gap-0 overflow-hidden rounded-xl border-grey4 bg-grey1 p-0"
      >
        <div className="flex h-11 flex-none items-center gap-2.5 border-b border-grey4 pl-4 pr-14">
          <FileText size={13} className="shrink-0 text-grey5" />
          <DialogTitle className="min-w-0 flex-1 truncate text-[12.5px] font-semibold text-white">
            {heading}
          </DialogTitle>
          <PdfPanePageIndicator current={page.current} total={page.total} />
          <PdfPaneZoomControl zoom={zoom} onChange={setZoom} />
          {onDownload && (
            <button
              type="button"
              aria-label={translate("MC_CS_CONF_EDIT_ARIA_DOWNLOAD_PDF")}
              onClick={onDownload}
              disabled={downloadPending}
              className="text-grey5 hover:text-white disabled:opacity-50"
            >
              {downloadPending ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Download size={14} />
              )}
            </button>
          )}
        </div>
        {failed ? (
          <div className="flex min-h-0 flex-1 items-center justify-center text-[12px] text-grey5">
            {translate("MC_CS_CONF_EDIT_PREVIEW_FAILED")}
          </div>
        ) : !url ? (
          <div className="flex min-h-0 flex-1 items-center justify-center gap-2 text-[12px] text-grey5">
            <Loader2 size={14} className="animate-spin" />
            {translate("MC_CS_CONF_EDIT_RENDERING")}
          </div>
        ) : (
          <div
            ref={bodyRef}
            className="flex min-h-0 flex-1 justify-center overflow-hidden"
          >
            <div
              className="flex min-h-0 max-w-full flex-col"
              style={{ width: boxWidth ?? "100%" }}
            >
              <DeclarationDocumentViewer
                className="min-h-0 flex-1"
                fileUrl={url}
                highlight={null}
                sources={[]}
                activeStatementId={null}
                open={barOpen}
                onOpenChange={setBarOpen}
                tokens={tokens}
                search={search}
                onTokensChange={setTokens}
                onSearchChange={setSearch}
                placeholder={translate("MC_DECL_V2_DOC_FIND_PLACEHOLDER")}
                scale={viewerScale}
                onCurrentPage={(current, total) => setPage({ current, total })}
              />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

// A4 is 1:1.4142; the viewer pads pages 18px top, 44px bottom and 20px per side.
const A4_RATIO = 1.4142
const PAGE_PAD_Y = 18 + 44
const PAGE_PAD_X = 40

function fitPageWidth(height: number): number {
  return Math.max(320, (height - PAGE_PAD_Y) / A4_RATIO + PAGE_PAD_X)
}

/** Box width and viewer `scale` for a zoom step: the page is `zoom` times its
 *  fit-page width, the box wraps it up to the overlay width, and `scale`
 *  carries only the overflow beyond that. Unmeasured ⇒ plain `zoom` scale. */
function zoomedBox(
  body: { width: number; fit: number } | null,
  zoom: number,
): { boxWidth: number | null; viewerScale: number | undefined } {
  if (!body) {
    return { boxWidth: null, viewerScale: zoom === 1 ? undefined : zoom }
  }
  const pageWidth = Math.max(0, body.fit - PAGE_PAD_X) * zoom
  const boxWidth = Math.floor(Math.min(body.width, pageWidth + PAGE_PAD_X))
  const inner = boxWidth - PAGE_PAD_X
  const overflow = inner > 0 ? pageWidth / inner : 1
  return { boxWidth, viewerScale: overflow > 1.001 ? overflow : undefined }
}
